import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import {
  TableColumn,
  TableColumnTypes,
  TableConfig,
  TableFooterValueTypes,
} from 'src/app/shared/components/table/table.model';

import { TableComponent } from '../../../shared/components/table/table.component';
import {
  BalanceTableRow,
  DeliveryOriginEnum,
  Cycle,
  BalanceSummaryRow,
} from '../../models/cycle.model';
import { getDepartures } from '../../store/selectors/departure.selectors';

@Component({
  selector: 'app-balance-summary',
  templateUrl: './balance-summary.component.html',
  styleUrls: ['./balance-summary.component.scss'],
  imports: [SummaryCardComponent, TableComponent, TranslatePipe],
})
export class BalanceSummaryComponent implements OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly locale = inject(LocaleService);

  @Input() balance: BalanceTableRow[];
  @Input() cycle: Cycle;
  @Output() allocationChanged: EventEmitter<BalanceTableRow[]> = new EventEmitter<
    BalanceTableRow[]
  >();

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: true,
    customFooter: [true],
    scrolling: true,
    wrapperWidth: '100%',
  };
  totalAmountOfChicks: number;
  summaryTableConfig: TableConfig;

  summaryColumns: TableColumn[] = [
    {
      id: 'productTitle',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('balance-summary.product'),
    },
    {
      id: 'initialStock',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('balance-summary.initial-stock'),
      footerDescription: this.locale.translate('total'),
      footerValueTypes: TableFooterValueTypes.SUM,
    },
    {
      id: 'deliveryStock',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('balance-summary.delivery-stock'),
      footerValueTypes: TableFooterValueTypes.SUM,
    },
    {
      id: 'totalConsumption',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('balance-summary.total'),
      customfooterValues: [],
      footerValueTypes: TableFooterValueTypes.SUM,
    },
    {
      id: 'finalStock',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('balance-summary.final-stock'),
      footerValueTypes: TableFooterValueTypes.SUM,
    },
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.summaryTableConfig = {
      ...this.tableConfigBase,
      dataSource: new MatTableDataSource([]),
    };

    this.initCustomFooterValues();

    if (!!this.balance && this.balance[0].barnBalanceRows.filter((row) => !!row.barn).length > 1) {
      this.summaryTableConfig.columns = this.summaryColumns;
    } else {
      this.summaryTableConfig.columns = this.summaryColumns.filter(
        (col) => col.id !== 'flockAllocation',
      );
    }
    this.store
      .select(getDepartures)
      .pipe(take(1))
      .subscribe((departures) => {
        this.totalAmountOfChicks = departures.reduce(
          (total, departure) => total + departure.amountOfChicks,
          0,
        );
        const datasource = this.calculateSummaryTable(this.totalAmountOfChicks);
        this.summaryTableConfig.dataSource = new MatTableDataSource(datasource);
      });
  }

  private initCustomFooterValues() {
    const customFooterValues = [
      this.locale.translate('balance-summary.consumption-per-delivered-chick'),
    ];

    const productColumn = this.summaryColumns.find((col) => col.id === 'productTitle');
    if (productColumn) {
      productColumn.customfooterValues = customFooterValues;
    }
  }

  updateSummaryRemainders(passedRemainder: number, passedRow: BalanceTableRow) {
    this.balance = [
      ...this.balance.filter((row) => row.balanceRowId !== passedRow.balanceRowId),
      {
        ...this.balance.find((row) => row.balanceRowId === passedRow.balanceRowId),
        remainder: Number(passedRemainder),
      },
    ];
    this.summaryTableConfig.dataSource.data = this.calculateSummaryTable(this.totalAmountOfChicks);
  }

  calculateSummaryTable(totalAmountOfChicks: number): BalanceSummaryRow[] {
    const summary: BalanceSummaryRow[] = [];
    this.balance.forEach((row) => {
      const existingRowIndex = summary.findIndex(
        (x) => x.productIdentifier === row.productIdentifier && x.productTitle === row.productTitle,
      );

      if (existingRowIndex >= 0) {
        summary[existingRowIndex].totalConsumption +=
          (isNaN(row.quantity) ? 0 : row.quantity) - (isNaN(row.remainder) ? 0 : row.remainder);

        summary[existingRowIndex].finalStock += row.remainder;

        if (row.deliverySource === DeliveryOriginEnum.START) {
          summary[existingRowIndex].initialStock += row.quantity;
        } else {
          summary[existingRowIndex].deliveryStock += row.quantity;
        }
      } else {
        summary.push({
          productIdentifier: row.productIdentifier,
          productTitle: row.productTitle,
          deliveryStock: row.deliverySource === DeliveryOriginEnum.START ? 0 : row.quantity,
          initialStock: row.deliverySource === DeliveryOriginEnum.START ? row.quantity : 0,
          finalStock: row.remainder,
          totalConsumption:
            (isNaN(row.quantity) ? 0 : row.quantity) - (isNaN(row.remainder) ? 0 : row.remainder),
        } as BalanceSummaryRow);
      }
    });

    const totalConsumption = summary.reduce(
      (total, row) => total + (isNaN(row.totalConsumption) ? 0 : row.totalConsumption),
      0,
    );

    const totalConsumptionColumn = this.summaryColumns.find(
      (column) => column.id === 'totalConsumption',
    );
    if (totalConsumptionColumn) {
      totalConsumptionColumn.customfooterValues = [
        `${(!!totalConsumption && !!totalAmountOfChicks
          ? Math.round((totalConsumption * 1000) / totalAmountOfChicks)
          : 0
        ).toLocaleString(this.locale.getLocale())} g`,
      ];
    }

    return summary;
  }
}
